import CustomButton from 'Components/CustomButton'
import SchoolCalendarDay from 'Services/SchoolManagement/Features/SchoolCalendar/Components/SchoolCalendarDay'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { IconButton, styled, Typography, useTheme } from '@mui/material'
import { Box, Grid } from '@mui/system'
import { LocalizationProvider, type PickersDayProps, StaticDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import fiLocale from 'date-fns/locale/fi'
import { memo, useEffect, useState } from 'react'
import { t } from 'ttag'

const StyledStaticDatePicker = styled(StaticDatePicker)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minWidth: '240px',
  width: '100%',
  '.MuiPickersCalendarHeader-labelContainer, .MuiPickersCalendarHeader-root': {
    display: 'none'
  },
  // Hide the navigation arrows
  '.MuiPickersArrowSwitcher-root': {
    display: 'none'
  },
  // Make the calendar fill the available space
  '.MuiDayCalendar-header, .MuiDayCalendar-weekContainer': {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    width: '100%',
    margin: '0px 0'
  },
  // Style for the days of the week headers
  '.MuiDayCalendar-weekDayLabel': {
    width: '100%',
    margin: 0,
    fontSize: 14,
    color: theme.palette.text.primary
  },
  // Make date buttons square with borders and larger
  '.MuiPickersDay-root': {
    borderRadius: '0',
    border: '1px solid #D5D4DF',
    margin: 0,
    height: '40px',
    width: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    fontSize: 14,
    '&.Mui-selected': {
      border: `1px solid ${theme.palette.background.default}`
    },
    '&.Mui-disabled:not(.Mui-selected)': {
      backgroundColor: theme.palette.background.paper
    }
  },
  '.MuiDateCalendar-root': {
    height: '100%',
    maxHeight: 'none',
    width: '100%'
  }
}))

interface SchoolCalendarCalendarProps {
  selectedYear: number
  months: Date[]
  handleDateSelect: (date: Date, monthIndex: number) => void
  isDateSelected: (date: Date, monthIndex: number) => boolean
  setSelectedDates: React.Dispatch<React.SetStateAction<Record<number, Date[]>>>
}

function SchoolCalendarCalendar({
  selectedYear,
  months,
  handleDateSelect,
  isDateSelected,
  setSelectedDates
}: Readonly<SchoolCalendarCalendarProps>) {
  const theme = useTheme()
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0)
  const [yearView, setYearView] = useState(true)

  useEffect(() => {
    setCurrentMonthIndex(0)
  }, [selectedYear, yearView])

  const handleNextMonth = () => {
    setCurrentMonthIndex(prev => (prev === months.length - 1 ? 0 : prev + 1))
  }

  const handlePrevMonth = () => {
    setCurrentMonthIndex(prev => (prev === 0 ? months.length - 1 : prev - 1))
  }

  const unSelectAllDays = () => {
    setSelectedDates({})
  }

  const selectAllDays = () => {
    const allDates: Record<number, Date[]> = {}
    months.forEach((month, index) => {
      allDates[index] = Array.from(
        { length: new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate() },
        (_, day) => new Date(month.getFullYear(), month.getMonth(), day + 1)
      )
    })
    setSelectedDates(allDates)
  }

  const selectAllDaysCurrentMonth = () => {
    const currentMonthDates: Date[] = Array.from(
      {
        length: new Date(
          months[currentMonthIndex].getFullYear(),
          months[currentMonthIndex].getMonth() + 1,
          0
        ).getDate()
      },
      (_, day) =>
        new Date(
          months[currentMonthIndex].getFullYear(),
          months[currentMonthIndex].getMonth(),
          day + 1
        )
    )
    setSelectedDates({ [currentMonthIndex]: currentMonthDates })
  }

  const selectAllWeekdays = () => {
    const weekdays: Record<number, Date[]> = {}
    months.forEach((month, index) => {
      const monthDates = Array.from(
        { length: new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate() },
        (_, day) => new Date(month.getFullYear(), month.getMonth(), day + 1)
      )
      weekdays[index] = monthDates.filter(date => date.getDay() !== 0 && date.getDay() !== 6)
    })
    setSelectedDates(weekdays)
  }

  const selectAllWeekdaysCurrentMonth = () => {
    const currentMonthDates = Array.from(
      {
        length: new Date(
          months[currentMonthIndex].getFullYear(),
          months[currentMonthIndex].getMonth() + 1,
          0
        ).getDate()
      },
      (_, day) =>
        new Date(
          months[currentMonthIndex].getFullYear(),
          months[currentMonthIndex].getMonth(),
          day + 1
        )
    )
    const weekdays = currentMonthDates.filter(date => date.getDay() !== 0 && date.getDay() !== 6)
    setSelectedDates({ [currentMonthIndex]: weekdays })
  }

  const toggleYearView = () => {
    setYearView(prev => !prev)
  }

  const renderCalendarDay = (props: PickersDayProps<Date>, monthIndex: number) => (
    <SchoolCalendarDay
      {...props}
      monthIndex={monthIndex}
      isSelected={isDateSelected(props.day, monthIndex)}
      onClick={() => handleDateSelect(props.day, monthIndex)}
      selected={false}
    />
  )

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={yearView ? selectAllWeekdays : selectAllWeekdaysCurrentMonth}
          >{t`Select weekdays`}</CustomButton>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={yearView ? selectAllDays : selectAllDaysCurrentMonth}
          >{t`Select all`}</CustomButton>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={unSelectAllDays}
          >{t`Unselect all`}</CustomButton>
        </Box>
        <CustomButton
          size="small"
          variant="text"
          onClick={toggleYearView}
          startIcon={<CalendarTodayIcon />}
        >
          {yearView ? t`Month view` : t`Year view`}
        </CustomButton>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fiLocale}>
        {yearView ? (
          <Grid container spacing={2} columnGap={0} rowGap={5}>
            {months.map((month, index) => (
              <Grid
                size={{ xs: 6, lg: 4, xl: 4 }}
                key={`calendar-month-${month}`}
                sx={{
                  borderRight: {
                    sm: (index + 1) % 2 === 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    lg: (index + 1) % 3 === 0 ? 'none' : `1px solid ${theme.palette.divider}`
                  }
                }}
              >
                <Box px={2}>
                  <Typography
                    variant="h5"
                    sx={{ textAlign: 'left', mb: 1, fontWeight: 700, fontFamily: 'Prompt' }}
                  >
                    {format(month, 'MMM yyyy', { locale: fiLocale })}
                  </Typography>
                  <StyledStaticDatePicker
                    key={`calendar-${selectedYear}-${month}`}
                    value={month}
                    displayStaticWrapperAs="desktop"
                    disableHighlightToday={true}
                    showDaysOutsideCurrentMonth
                    shouldDisableDate={date => {
                      // Disable dates that are not in the current month
                      return (
                        date.getMonth() !== month.getMonth() ||
                        date.getFullYear() !== month.getFullYear()
                      )
                    }}
                    dayOfWeekFormatter={day => {
                      return format(day, 'EEEEEE', { locale: fiLocale })
                    }}
                    slots={{
                      day: props => renderCalendarDay({ ...props }, index)
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <IconButton onClick={handlePrevMonth} size="large">
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                variant="h5"
                sx={{ textAlign: 'center', fontWeight: 700, fontFamily: 'Prompt' }}
              >
                {format(months[currentMonthIndex], 'MMM yyyy', { locale: fiLocale })}
              </Typography>
              <IconButton onClick={handleNextMonth} size="large">
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                transition: 'opacity 0.3s ease-in-out',
                opacity: 1
              }}
            >
              <StyledStaticDatePicker
                key={`calendar-${selectedYear}-${currentMonthIndex}`}
                value={months[currentMonthIndex]}
                displayStaticWrapperAs="desktop"
                disableHighlightToday={true}
                showDaysOutsideCurrentMonth
                shouldDisableDate={date => {
                  return (
                    date.getMonth() !== months[currentMonthIndex].getMonth() ||
                    date.getFullYear() !== months[currentMonthIndex].getFullYear()
                  )
                }}
                dayOfWeekFormatter={day => format(day, 'EEEEEE', { locale: fiLocale })}
                slots={{ day: props => renderCalendarDay({ ...props }, currentMonthIndex) }}
                sx={{
                  '.MuiPickersDay-root': {
                    height: '91px',
                    justifyContent: 'end',
                    paddingRight: 2,
                    paddingBottom: 5
                  },
                  '.MuiPickersSlideTransition-root': {
                    height: '570px'
                  }
                }}
              />
            </Box>
          </Box>
        )}
      </LocalizationProvider>
    </Box>
  )
}

export default memo(SchoolCalendarCalendar)
